if (!customElements.get('innerstate-routine')) {
  customElements.define('innerstate-routine', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = {signal:this.controller.signal};
      this.track = this.querySelector('.is-routine__track');
      this.cards = [...this.track.children];
      this.navigation = this.querySelector('.is-routine__navigation');
      this.prev = this.querySelector('[data-routine-prev]');
      this.next = this.querySelector('[data-routine-next]');
      this.pages = this.querySelector('[data-routine-pages]');
      this.counter = this.querySelector('[data-routine-counter]');
      this.status = this.querySelector('.is-routine__status');
      this.modalCache = new Map();
      this.openSequence = 0;
      this.prev.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.pages?.addEventListener('click', event => {
        const page = event.target.closest('[data-routine-page]');
        if (page) this.goTo(Number(page.dataset.routinePage));
      }, options);
      this.track.addEventListener('scroll', () => this.update(), {...options,passive:true});
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, options);
      this.addEventListener('click', event => {
        const trigger = event.target.closest('[data-routine-choose]');
        if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (typeof HTMLDialogElement === 'undefined') return;
        event.preventDefault();
        this.openPicker(trigger);
      }, options);
      this.observer = new ResizeObserver(() => this.update());
      this.observer.observe(this.track);
      this.update();
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.modalFetch?.abort();
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
    }
    step() {
      const step = this.cards.length > 1 ? this.cards[1].offsetLeft - this.cards[0].offsetLeft : this.track.clientWidth;
      return step > 0 ? step : 0;
    }
    move(direction) {
      this.goTo(this.currentPage + direction);
    }
    goTo(index) {
      if (!this.pageStops?.length) return;
      const page = Math.max(0, Math.min(index, this.pageStops.length - 1));
      this.track.scrollTo({left:this.pageStops[page],behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const step = this.step();
      if (!step || max < 2) { this.navigation.hidden = true; return; }
      this.navigation.hidden = false;
      const last = Math.ceil((max - 2) / step);
      this.pageStops = Array.from({length:last + 1}, (_, index) => Math.min(max, index * step));
      const left = this.track.scrollLeft;
      this.currentPage = this.pageStops.reduce((closest, stop, index) =>
        Math.abs(left - stop) < Math.abs(left - this.pageStops[closest]) ? index : closest, 0);
      this.prev.disabled = left < 2;
      this.next.disabled = left >= max - 2;
      if (!this.pages) {
        if (this.counter) this.counter.textContent = `${this.currentPage + 1} / ${this.pageStops.length}`;
        return;
      }
      if (this.pages.children.length !== this.pageStops.length) {
        this.pages.replaceChildren(...this.pageStops.map((_, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'is-routine__page';
          button.dataset.routinePage = index;
          button.setAttribute('aria-label', `${this.pages.dataset.label} ${index + 1} / ${this.pageStops.length}`);
          button.setAttribute('aria-controls', this.track.id);
          return button;
        }));
      }
      [...this.pages.children].forEach((button, index) => {
        button.setAttribute('aria-current', index === this.currentPage ? 'true' : 'false');
      });
    }
    createPicker() {
      if (this.dialog) return;
      const dialog = document.createElement('dialog');
      dialog.id = 'innerstate-routine-picker';
      dialog.className = 'is-routine-modal';
      dialog.setAttribute('aria-label','Product bekijken en toevoegen');
      dialog.innerHTML = '<div class="is-routine-modal__panel"><button type="button" class="is-routine-modal__close" aria-label="Sluiten"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button><div class="is-routine-modal__scroll"><div class="is-routine-modal__content"></div><p class="is-routine-modal__status" role="status" aria-live="polite"></p></div></div>';
      const options = {signal:this.controller.signal};
      dialog.querySelector('.is-routine-modal__close').addEventListener('click', () => dialog.close(), options);
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        dialog.close();
      }, options);
      dialog.addEventListener('close', () => {
        ++this.openSequence;
        this.modalFetch?.abort();
        this.returnFocus?.focus({preventScroll:true});
      }, options);
      dialog.addEventListener('submit', event => {
        const form = event.target.closest('[data-iw-form]');
        if (!form || event.defaultPrevented) return;
        event.preventDefault();
        event.stopPropagation();
        this.add(form,event.submitter);
      }, options);
      this.appendChild(dialog);
      this.dialog = dialog;
      this.modalContent = dialog.querySelector('.is-routine-modal__content');
      this.modalStatus = dialog.querySelector('.is-routine-modal__status');
    }
    async openPicker(trigger) {
      this.createPicker();
      const url = new URL(trigger.href,window.location.origin);
      if (url.origin !== window.location.origin) { window.location.assign(trigger.href); return; }
      const key = url.pathname;
      const sequence = ++this.openSequence;
      this.returnFocus = trigger;
      this.modalStatus.textContent = '';
      for (const node of this.modalCache.values()) node.hidden = true;
      if (!this.dialog.open) {
        this.dialog.showModal();
      }
      this.dialog.setAttribute('aria-label',`${trigger.dataset.productTitle || 'Product'} bekijken en toevoegen`);
      trigger.setAttribute('aria-busy','true');
      this.modalStatus.textContent = 'Product laden…';
      try {
        let product = this.modalCache.get(key);
        if (!product) {
          url.searchParams.set('view','routine-picker');
          this.modalFetch?.abort();
          this.modalFetch = new AbortController();
          const response = await fetch(url.href,{signal:this.modalFetch.signal,credentials:'same-origin'});
          if (!response.ok) throw new Error(this.dataset.error);
          const parsed = new DOMParser().parseFromString(await response.text(),'text/html');
          const source = parsed.querySelector('[data-routine-picker-product]');
          if (!source) throw new Error(this.dataset.error);
          if (sequence !== this.openSequence || !this.dialog.open) return;
          // Alleen de prijswidget-init uit onze eigen Liquid-template wordt uitgevoerd.
          // Per product blijft één widget gemonteerd; heropenen registreert geen extra handlers.
          const initScripts = [...source.querySelectorAll('script[data-iw-init]')].map(script => script.textContent);
          source.querySelectorAll('script').forEach(script => script.remove());
          product = document.importNode(source,true);
          product.hidden = true;
          this.modalContent.appendChild(product);
          for (const code of initScripts) {
            const script = document.createElement('script');
            script.textContent = code;
            product.appendChild(script);
            script.remove();
          }
          this.modalCache.set(key,product);
        }
        if (sequence !== this.openSequence || !this.dialog.open) return;
        product.hidden = false;
        this.dialog.querySelector('.is-routine-modal__scroll').scrollTop = 0;
        this.modalStatus.textContent = '';
        product.querySelector('.is-routine-modal__title')?.focus({preventScroll:true});
      } catch(error) {
        if (error.name === 'AbortError' || sequence !== this.openSequence) return;
        this.modalStatus.textContent = `${this.dataset.error} `;
        const link = document.createElement('a');
        link.href = trigger.href;
        link.textContent = 'Bekijk de productpagina';
        this.modalStatus.appendChild(link);
      } finally {
        trigger.removeAttribute('aria-busy');
      }
    }
    async add(form,submitter) {
      if (this.busy) return;
      const button = submitter || form.querySelector('button[type="submit"]');
      if (!button || button.disabled) return;
      const values = new FormData(form);
      const item = {id:values.get('id'),quantity:Number(values.get('quantity')) || 1};
      const plan = values.get('selling_plan');
      if (plan) item.selling_plan = plan;
      this.busy = true;
      const original = button.textContent;
      button.disabled = true;
      button.textContent = this.dataset.loading;
      button.setAttribute('aria-busy','true');
      this.modalStatus.textContent = '';
      let added = false;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const helper = window.Alpine?.store('xCartHelper');
        const sections = helper?.getSectionsToRender() || [];
        const payload = {items:[item],sections_url:window.location.pathname};
        if (sections.length) payload.sections = sections.map(section => section.id).slice(0,5);
        const response = await fetch(`${root}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify(payload)});
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || this.dataset.error);
        added = true;
        const parser = new DOMParser();
        for (const section of sections) {
          const target = document.querySelector(section.selector);
          const html = result.sections?.[section.id];
          if (!target || !html) continue;
          const source = parser.parseFromString(html,'text/html').querySelector(section.selector);
          if (source) target.innerHTML = source.innerHTML;
        }
        if (helper) {
          const bubble = document.querySelector('#cart-icon-bubble span');
          if (bubble) helper.currentItemCount = parseInt(bubble.textContent,10) || 0;
        }
        document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
        // Eerst de modale focus- en scrollblokkering opruimen, daarna de cartdrawer openen.
        if (this.dialog.open) {
          const closed = new Promise(resolve => this.dialog.addEventListener('close',resolve,{once:true}));
          this.dialog.close();
          await closed;
        }
        this.status.textContent = this.dataset.added;
        const mini = window.Alpine?.store('xMiniCart');
        if (mini) mini.openCart();
      } catch(error) {
        this.modalStatus.textContent = added ? this.dataset.added : (error.message || this.dataset.error);
        if (added && this.dialog.open) this.dialog.close();
      } finally {
        this.busy = false;
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = original;
      }
    }
  });
}

